<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="eventGroupForm.id ? $t('tournament_details.edit_tournament.dialog.header') : $t('new_tournament.dialog.header')"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputTitle"
          rules="required"
        >
          <v-text-field
            v-model="eventGroupForm.title"
            :error-messages="errors"
            :label="$t('text_input.title.title')"
            :placeholder="$t('text_input.title.placeholder')"
            outlined
          />
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-calendar-alt
          </v-icon> {{ $t('picker.date.tournament_duration.title') }}
        </subtitle>

        <v-row no-gutters>
          <v-col
            cols="6"
            class="pr-2"
          >
            <validation-provider
              v-slot="{ errors }"
              key="eventGroupFormInputStartDate"
              rules="required"
            >
              <v-menu
                v-model="dateFromActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="eventGroupForm.start_date ? $dayjs(eventGroupForm.start_date).format('MMM D, YYYY') : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.select_start_date.title')"
                    outlined
                    dense
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="eventGroupForm.start_date"
                  :first-day-of-week="1"
                  no-title
                  @input="validateDateRange; dateFromActive = false"
                />
              </v-menu>
            </validation-provider>
          </v-col>
          <v-col
            cols="6"
            class="pl-2"
          >
            <validation-provider
              v-slot="{ errors }"
              key="eventGroupFormInputEndDate"
              rules="required"
            >
              <v-menu
                v-model="dateToActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="eventGroupForm.end_date ? $dayjs(eventGroupForm.end_date).format('MMM D, YYYY') : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.select_end_date.title')"
                    outlined
                    dense
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="eventGroupForm.end_date"
                  :first-day-of-week="1"
                  no-title
                  @input="validateDateRange; dateToActive = false"
                />
              </v-menu>
            </validation-provider>
          </v-col>
        </v-row>

        <v-alert
          v-if="endBeforeStart"
          class="caption pa-2 mt-2"
          type="warning"
          outlined
          color="orange darken-2"
          icon="fal fa-exclamation-triangle"
        >
          {{ $t('tournament_details.date_range_alert') }}
        </v-alert>

        <subtitle class="mb-3 mt-4">
          <v-icon
            left
            small
          >
            fal fa-align-left
          </v-icon> {{ $t('text_input.description.title') }}
        </subtitle>

        <rich-text-editor
          v-model="eventGroupForm.description"
          class="mb-6"
        />

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user-group
          </v-icon> {{ $t('add_new.team') }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputTeams"
        >
          <v-autocomplete
            v-model="eventGroupForm.team_ids"
            outlined
            :search-input.sync="searchInputTeams"
            :items="teamSelectItems"
            item-value="id"
            item-text="name"
            dense
            multiple
            :error-messages="errors"
            :placeholder="$t('select_team.placeholder')"
          >
            <template v-slot:selection="{item}">
              {{ item.name }}
            </template>
          </v-autocomplete>
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("dropdown.players.title") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputPlayers"
        >
          <v-autocomplete
            v-model="eventGroupForm.player_ids"
            outlined
            :search-input.sync="searchInputPlayers"
            :items="playersToSelect"
            item-value="id"
            item-text="name"
            dense
            multiple
            :chips="false"
            :error-messages="errors"
            clearable
            :placeholder="$t('dropdown.players.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 2"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 2">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 2"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
            <template v-slot:item="{ item, on }">
              <v-list-item-action>
                <v-simple-checkbox
                  :value="eventGroupForm.player_ids.indexOf(item.id) > -1"
                  :ripple="false"
                  v-on="on"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span>{{ item.team.short_name }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  > &middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>

        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-user
          </v-icon> {{ $t("eventDetails.reservations.coaches") }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          key="eventGroupFormInputCoaches"
        >
          <v-autocomplete
            v-model="eventGroupForm.coach_ids"
            outlined
            :search-input.sync="searchInputCoaches"
            :items="coaches.data"
            :loading="coaches.loading"
            item-value="id"
            :item-text="(coach) => coach.first_name + ' ' + coach.last_name"
            dense
            multiple
            clearable
            :error-messages="errors"
            :placeholder="$t('eventDetails.reservations.coaches.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </validation-provider>
      </validation-observer>
    </v-card-text>

    <v-card-actions class="pa-6 pt-0">
      <v-btn
        v-if="eventGroupForm.id && $can('edit-event')"
        color="error"
        text
        @click="initDelete()"
      >
        {{ $t('button.delete') }}
      </v-btn>
      <v-spacer />
      <v-btn
        text
        @click="modalVisible = false"
      >
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        {{ $t('button.save') }}
      </v-btn>
    </v-card-actions>
    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteEventGroup()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import { CoachApiService, EventGroupApiService, PlayerApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { cloneDeep, difference, intersection, union } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import dayjs from 'dayjs'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'

const emptyForm = {
  title: '',
  description: '',
  start_date: dayjs().format('YYYY-MM-DD'),
  end_date: dayjs().add(15, 'day').format('YYYY-MM-DD'),
  team_ids: [],
  player_ids: [],
  coach_ids: []
}

export default {
  name: 'TournamentFormDialog',
  components: { RichTextEditor, Subtitle, CardDialog, DeleteConfirmationDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    eventGroup: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateToActive: false,
      dateFromActive: false,
      eventGroupForm: cloneDeep(emptyForm),
      submitting: false,
      searchInputTeams: '',
      searchInputPlayers: '',
      searchInputCoaches: '',
      suppressIdsWatch: false,
      players: {
        data: [],
        loading: false,
        eventPlayers: []
      },
      coaches: {
        data: [],
        loading: false
      }
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    endBeforeStart() {
      if (!this.eventGroupForm.start_date || !this.eventGroupForm.end_date) {
        return false
      }

      const startDate = dayjs(this.eventGroupForm.start_date)
      const endDate = dayjs(this.eventGroupForm.end_date)

      return endDate.isBefore(startDate)
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    playersToSelect() {
      return this.players.data.slice(0).sort((a, b) => {
        const aSelected = this.eventGroupForm.player_ids.indexOf(a.id) > -1
        const bSelected = this.eventGroupForm.player_ids.indexOf(b.id) > -1

        if (aSelected && !bSelected) return -1
        else if (!aSelected && bSelected) return 1

        return 0
      })
    }
  },
  watch: {
    'eventGroupForm.team_ids': function (newValue, oldValue) {
      this.searchInputTeams = ''

      if (this.suppressIdsWatch) return

      const addedTeamIds = difference(newValue, oldValue)
      const removedTeamIds = difference(oldValue, newValue)

      const removePlayerIds = this.playersToSelect.filter(p => removedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)
      const addPlayerIds = this.playersToSelect.filter(p => addedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)

      this.eventGroupForm.player_ids = union(difference(this.eventGroupForm.player_ids, removePlayerIds), addPlayerIds)

      const removeCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), removedTeamIds).length > 0).map(c => c.id)
      const addCoachIds = this.coaches.data.filter(c => intersection(c.teams.filter(t => t.is_coach).map(t => t.id), addedTeamIds).length > 0).map(c => c.id)

      this.eventGroupForm.coach_ids = union(difference(this.eventGroupForm.coach_ids, removeCoachIds), addCoachIds)
    },
    'eventGroupForm.player_ids': function () {
      this.searchInputPlayers = ''
    },
    'eventGroupForm.coach_ids': function () {
      this.searchInputCoaches = ''
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_TOURNAMENT_FORM, (eventGroup) => {
      this.resetForm()

      if (eventGroup && eventGroup.id) {
        this.loadEventGroup(eventGroup.id)
      }

      this.fetchAllPlayers()
      this.fetchAllCoaches()
      this.modalVisible = true
    })

    EventBus.$on(this.$events.EDIT_EVENT_GROUP, (eventGroupId) => {
      this.resetForm()

      if (eventGroupId) {
        this.loadEventGroup(eventGroupId)
        this.fetchAllPlayers()
        this.fetchAllCoaches()
        this.modalVisible = true
      }
    })
  },
  methods: {
    resetForm() {
      this.eventGroupForm = cloneDeep(emptyForm)
      this.searchInputTeams = ''
      this.searchInputPlayers = ''
      this.searchInputCoaches = ''
      this.suppressIdsWatch = false
    },
    async loadEventGroup(id) {
      try {
        this.suppressIdsWatch = true

        const response = await EventGroupApiService.get(id)
        this.eventGroupForm = {
          id: response.data.id,
          title: response.data.title,
          description: response.data.description,
          start_date: response.data.start_date,
          end_date: response.data.end_date,
          team_ids: response.data.teams ? response.data.teams.map(team => team.id) : [],
          player_ids: response.data.players ? response.data.players.map(player => player.id) : [],
          coach_ids: response.data.coaches ? response.data.coaches.map(coach => coach.id) : []
        }
        this.players.eventPlayers = response.data.players || []

        this.$nextTick(() => { this.suppressIdsWatch = false })
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.load_tournament'),
          color: 'error'
        })
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({})

        this.coaches.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.coaches.loading = false
      }
    },
    validateDateRange() {
      if (this.endBeforeStart) {
        this.eventGroupForm.end_date = this.eventGroupForm.start_date
      }
    },
    initDelete() {
      this.deleteConfirmationDialogVisible = true
    },
    async deleteEventGroup() {
      try {
        this.deleting = true

        await EventGroupApiService.delete(this.eventGroupForm.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('success.tournament_deleted'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false
        EventBus.$emit(this.$events.EVENT_GROUP_DELETED, this.eventGroupForm)
        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.delete_tournament'),
          color: 'error'
        })
      } finally {
        this.deleting = false
      }
    },
    async submit() {
      const isValid = await this.$refs.validation.validate()

      if (!isValid) {
        return
      }

      this.submitting = true

      try {
        let response
        const formData = {
          title: this.eventGroupForm.title,
          description: this.eventGroupForm.description,
          start_date: this.eventGroupForm.start_date,
          end_date: this.eventGroupForm.end_date,
          team_ids: this.eventGroupForm.team_ids,
          player_ids: this.eventGroupForm.player_ids,
          coach_ids: this.eventGroupForm.coach_ids
        }

        if (this.eventGroupForm.id) {
          response = await EventGroupApiService.update(this.eventGroupForm.id, formData)
          EventBus.$emit(this.$events.EVENT_GROUP_UPDATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.tournament_updated'),
            color: 'success'
          })
        } else {
          response = await EventGroupApiService.create(formData)
          EventBus.$emit(this.$events.EVENT_GROUP_CREATED, response.data)
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('success.tournament_created'),
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('error.save_tournament'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
